import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { BackButton, Button } from '../components/common';
import { Heading, Text } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { FormSection, FormField, Input } from '../components/common/Form';

const getNoSessionMessage = () =>
  'No valid reset link was found. This often happens when the link is opened in a different browser or device, ' +
  'or the link was already used (they are single-use). Please use "Forgot password?" on the login page to request a new link.';

/** Session can lag briefly after setSession() or storage sync — retry before showing a false "no link" error */
const getSessionWithRetry = async (maxAttempts = 6, delayMs = 120) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const { data, error: sessionErr } = await supabase.auth.getSession();
    if (sessionErr) {
      console.error('getSession error:', sessionErr);
      break;
    }
    if (data?.session) return data;
    if (attempt < maxAttempts - 1) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  return { session: null };
};

const ResetPassword = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasValidSession, setHasValidSession] = useState(false);
  /** False until initial token/session handling finishes — avoids submit before setSession() persists */
  const [authInitializing, setAuthInitializing] = useState(true);
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });

  // Parse tokens from both hash and query (hash is Supabase default; query can appear when link is opened in another browser or after redirects)
  const getTokensFromUrl = () => {
    const hash = window.location.hash.substring(1);
    const search = window.location.search.substring(1);
    const hashParams = new URLSearchParams(hash);
    const searchParams = new URLSearchParams(search);
    return {
      accessToken: hashParams.get('access_token') || searchParams.get('access_token'),
      refreshToken: hashParams.get('refresh_token') || searchParams.get('refresh_token')
    };
  };

  // Handle Supabase session from URL parameters
  useEffect(() => {
    const handleAuthCallback = async () => {
      setAuthInitializing(true);
      try {
        const { accessToken, refreshToken } = getTokensFromUrl();

        if (accessToken && refreshToken) {
          // Set the session from URL parameters
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('Session setting error:', error);
            setError(
              'This reset link is invalid or has already been used. Password reset links are single-use. ' +
              'Please go back to the login page and request a new reset link.'
            );
            setHasValidSession(false);
          } else {
            // Confirm session is readable (avoids race if user submits before storage sync)
            const confirm = await getSessionWithRetry(8, 100);
            if (confirm?.session) {
              setHasValidSession(true);
              setError(null);
            } else {
              console.error('setSession succeeded but getSession still empty after retries');
              setError(getNoSessionMessage());
              setHasValidSession(false);
            }
          }
        } else {
          // No tokens in URL — may be recovery session already (e.g. hash stripped after setSession) or user opened link twice / Strict Mode re-run
          const data = await getSessionWithRetry(6, 100);

          if (data?.session) {
            setHasValidSession(true);
            setError(null);
          } else {
            setError(getNoSessionMessage());
            setHasValidSession(false);
          }
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('An error occurred while processing the reset link. Please request a new link from the login page.');
        setHasValidSession(false);
      } finally {
        setAuthInitializing(false);
      }
    };

    handleAuthCallback();
  }, []);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (authInitializing) return;

    setLoading(true);
    setError(null);

    try {
      // Ensure we have a session (avoid "Auth session missing!" and show friendly message instead)
      const sessionData = await getSessionWithRetry();
      if (!sessionData?.session) {
        setError(getNoSessionMessage());
        setLoading(false);
        return;
      }

      // Validate passwords match
      if (passwordData.password !== passwordData.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      // Validate password length
      if (passwordData.password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      // Update password using Supabase
      const { error } = await supabase.auth.updateUser({
        password: passwordData.password
      });

      if (error) throw error;

      // After successful password change, sign out then go straight to login with a flash message
      // (avoid delayed navigation — user stays on this route until then, which avoids confusing UI states)
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) {
        console.error('Error signing out after password reset:', signOutError);
      }

      navigate('/login', {
        replace: true,
        state: {
          loginFlash: 'Your password was updated. Please sign in with your new password.'
        }
      });

    } catch (err) {
      console.error('Error updating password:', err);
      setError(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Heading level={1} className="text-3xl font-bold text-text-primary mb-2">
            Reset Password
          </Heading>
          <Text className="text-text-secondary">
            {authInitializing ? 'Verifying your reset link…' : 'Enter your new password below'}
          </Text>
        </div>

        <Card variant="default" size="large">
            {authInitializing ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4" aria-busy="true" aria-live="polite">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
                <Text className="text-text-secondary text-sm text-center">
                  Confirming your session — you can set a new password in a moment.
                </Text>
              </div>
            ) : (
              <>
            {/* Error Message */}
            {error && (
              <Card variant="danger" size="small" className="mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-danger-dark" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-danger-dark">{error}</p>
                  </div>
                </div>
              </Card>
            )}

            <form onSubmit={handlePasswordReset} className="space-y-6">
              <FormSection>
                <FormField label="New Password" helperText="Password must be at least 6 characters long">
                  <Input
                    type="password"
                    value={passwordData.password}
                    onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                    required
                    disabled={!hasValidSession}
                    placeholder="Enter your new password"
                  />
                </FormField>

                <FormField label="Confirm New Password">
                  <Input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                    disabled={!hasValidSession}
                    placeholder="Confirm your new password"
                  />
                </FormField>
              </FormSection>

              <div className="flex flex-col space-y-3">
                <Button
                  type="submit"
                  disabled={loading || !hasValidSession}
                  variant="primary"
                  className="w-full"
                >
                  {loading ? 'Updating Password...' : 'Update Password'}
                </Button>

                <BackButton
                  onClick={() => navigate('/login')}
                  className="w-full"
                >
                  Back to Login
                </BackButton>
                {error && (
                  <p className="text-center text-sm text-text-secondary mt-2">
                    Use &quot;Forgot password?&quot; on the login page to request a new link.
                  </p>
                )}
              </div>
            </form>
              </>
            )}
        </Card>

        <div className="text-center">
          <Text className="text-sm text-text-muted">
            Need help? Contact support for assistance.
          </Text>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
